/** Runs a coded strategy / indicator with Python in the browser (Pyodide, in its own Web Worker) and
 *  hands the result to the backtest: position per candle (strategies) or line values (indicators). */

import { PYTHON_PRELUDE } from "../pages/pythonPrelude";

/** Python helpers added after the chart helpers, in the editor AND in backtest runs, so code that works
 *  in the editor works the same in the backtest list. */
export const LAB_HELPERS = String.raw`
# ---- Backtest helpers ---------------------------------------------------------------------------------------
# param(name, default, min, max, step)  a setting you can change from the backtest list without opening the code
# output(values, name="Value")          publish an indicator line so no-code rules can use it (also draws it)
# position = ...                        strategies: 1 = long, -1 = short, 0 = flat, one value per candle
position = None
_lwt_param_specs = []
_lwt_outputs = []
try:
    _lwt_param_values
except NameError:
    _lwt_param_values = {}


def param(name, default, min=None, max=None, step=None, label=None):
    """A number setting. In the editor it is 'default'; in the backtest list it is whatever you typed there."""
    key = str(name)[:30]
    value = _lwt_param_values.get(key, default)
    try:
        value = float(value)
    except (TypeError, ValueError):
        value = float(default)
    if min is not None and value < min:
        value = min
    if max is not None and value > max:
        value = max
    if isinstance(default, int) and not isinstance(default, bool) and float(value).is_integer():
        value = int(value)
    if not any(p["key"] == key for p in _lwt_param_specs):
        _lwt_param_specs.append({"key": key, "label": str(label or key)[:30], "value": default, "min": min, "max": max, "step": step})
    return value


def output(values, name="Value", pane="oscillator", color=None):
    """Publish an indicator line (one number per candle). Rules in the backtest list can compare it."""
    vals = _lwt_list(values)
    if len(vals) != len(candles):
        raise ValueError("output('%s'): got %d values but there are %d candles (one value per candle)" % (name, len(vals), len(candles)))
    cleaned = []
    for v in vals:
        try:
            f = float(v)
        except (TypeError, ValueError):
            f = None
        if f is not None and (f != f or f in (float("inf"), float("-inf"))):
            f = None
        cleaned.append(f)
    _lwt_outputs.append({"name": str(name)[:30], "values": cleaned})
    plot(name, vals, pane=pane, color=color)
    return values
`;

/** Appended after the user's code in backtest runs: its value (the last expression) comes back as JSON. */
export const LAB_EPILOGUE = String.raw`

def _lwt_lab_result():
    import json as _j
    pos = globals().get("position")
    out = {"params": _lwt_param_specs, "outputs": _lwt_outputs, "position": None}
    if pos is not None:
        vals = _lwt_list(pos)
        if len(vals) != len(candles):
            raise ValueError("position has %d values but there are %d candles (one value per candle)" % (len(vals), len(candles)))
        cleaned = []
        for v in vals:
            try:
                f = float(v)
            except (TypeError, ValueError):
                f = 0.0
            if f != f:
                f = 0.0
            cleaned.append(1 if f > 0 else (-1 if f < 0 else 0))
        out["position"] = cleaned
    return _j.dumps(out)


_lwt_lab_result()
`;

/** Prelude for a backtest run: chart helpers + backtest helpers, with the list's param values filled in. */
export function labPrelude(paramValues = {}) {
  const clean = {};
  Object.entries(paramValues || {}).forEach(([k, v]) => {
    const n = Number(v);
    if (Number.isFinite(n)) clean[String(k).slice(0, 30)] = n;
  });
  // JSON is valid Python for a dict of numbers; the backslashes keep it inside a Python string safely.
  const literal = JSON.stringify(JSON.stringify(clean));
  return `import json as _lwt_pj\n_lwt_param_values = _lwt_pj.loads(${literal})\n` + PYTHON_PRELUDE + LAB_HELPERS;
}

/** Turn the JSON the epilogue returned into {position, outputs, params}. Pure, unit-tested. */
export function parseLabResult(text, candleCount) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("The Python code did not return a result (did it finish?).");
  }
  const position = Array.isArray(data?.position) ? data.position.map((v) => (v > 0 ? 1 : v < 0 ? -1 : 0)) : null;
  if (position && position.length !== candleCount) throw new Error(`position has ${position.length} values but there are ${candleCount} candles.`);
  const outputs = {};
  (Array.isArray(data?.outputs) ? data.outputs : []).forEach((o) => {
    if (o && typeof o.name === "string" && Array.isArray(o.values) && o.values.length === candleCount) {
      outputs[o.name] = o.values.map((v) => (v === null || v === undefined ? NaN : Number(v)));
    }
  });
  return { position, outputs, params: Array.isArray(data?.params) ? data.params : [] };
}

/** Same candles? (cheap fingerprint for the cache). Pure. */
export function candleSignature(candles) {
  if (!candles?.length) return "0";
  const a = candles[0];
  const z = candles[candles.length - 1];
  return `${candles.length}:${a.time}:${a.close}:${z.time}:${z.close}`;
}

const RUN_TIMEOUT_MS = 120000; // first run also downloads Python (~10 MB) and any packages
const CACHE_LIMIT = 40;

let worker = null;
let queue = Promise.resolve();
let seq = 0;
const cache = new Map();

function getWorker() {
  if (!worker) worker = new Worker(`${process.env.PUBLIC_URL || ""}/pyodide-worker.js`);
  return worker;
}

function resetWorker() {
  if (worker) worker.terminate();
  worker = null;
}

function runOnce({ code, candles, paramValues, packages }) {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    seq += 1;
    const id = `lab-${seq}`;
    const stderr = [];
    let timer = null;
    const finish = (fn, value) => {
      clearTimeout(timer);
      w.removeEventListener("message", onMessage);
      fn(value);
    };
    const onMessage = (event) => {
      const msg = event.data || {};
      if (msg.type === "stderr") stderr.push(msg.text);
      else if (msg.type === "fatal") {
        resetWorker();
        finish(reject, new Error(msg.message || "Python could not start."));
      } else if (msg.id === id && msg.type === "done") finish(resolve, msg.result);
      else if (msg.id === id && msg.type === "error") {
        const text = String(msg.message || "Python error");
        // keep the useful end of a Python traceback
        const lines = text.trim().split("\n");
        finish(reject, new Error(lines.slice(-3).join(" ").slice(0, 400)));
      }
    };
    w.addEventListener("message", onMessage);
    timer = setTimeout(() => {
      resetWorker();
      finish(reject, new Error(`Python took longer than ${RUN_TIMEOUT_MS / 1000} s and was stopped.`));
    }, RUN_TIMEOUT_MS);
    w.postMessage({
      type: "run",
      id,
      code: `${code}\n${LAB_EPILOGUE}`,
      candles,
      packages: packages || [],
      prelude: labPrelude(paramValues),
    });
  });
}

/** Run code on candles. Results are cached, and runs go one at a time (one Python interpreter). */
export function runLabCode({ code, candles, paramValues = {}, packages = [] }) {
  const key = [code, JSON.stringify(paramValues), (packages || []).join(","), candleSignature(candles)].join("\u0000");
  if (cache.has(key)) return cache.get(key);
  const slim = candles.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume || 0 }));
  const job = queue.then(() => runOnce({ code, candles: slim, paramValues, packages })).then((text) => parseLabResult(text, candles.length));
  queue = job.catch(() => {});
  cache.set(key, job);
  job.catch(() => cache.delete(key)); // failures are not cached: fix the code and run again
  if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  return job;
}

/** Tests only. */
export function _clearLabCache() {
  cache.clear();
}
